import { useEffect, useCallback } from 'react';
import useGameStore from '../stores/gameStore';
import useUIStore from '../stores/uiStore';
import { useWebSocket } from './useWebSocket';

export const useGameState = () => {
  const gameStore = useGameStore();
  const uiStore = useUIStore();
  const websocket = useWebSocket();

  // Handle option selection with WebSocket integration
  const selectOption = useCallback(async (optionId: string): Promise<boolean> => {
    const { currentSession, currentPlayer } = gameStore;
    
    if (!currentSession || !currentPlayer) {
      uiStore.setError({ code: 'GAME_ERROR', message: 'No active session or player' });
      return false;
    }

    try {
      uiStore.setLoading(true, 'Processing your choice...');
      if (typeof gameStore.flushPendingUserInputs === 'function') {
        await gameStore.flushPendingUserInputs();
      }
      
      // Check if this is a finish option using the options map
      const option = gameStore.optionsMap[optionId];
      const isFinishOption = option?.target === 'FINISH';
      
      // For finish options, use the dedicated finish option method
      if (isFinishOption) {
        await gameStore.selectFinishOption();
        return true; // Finish option always completes the adventure
      }
      
      // Send via WebSocket for real-time updates for regular options
      if (websocket.isConnected) {
        websocket.sendPlayerAction(currentSession.id, currentPlayer.id, optionId);
        return false; // WebSocket doesn't return completion status for regular options
      } else {
        // Fallback to REST API
        const result = await gameStore.selectOption(optionId);
        return result;
      }
      
    } catch (error) {
      uiStore.setError(error as any, 'Failed to process choice');
      throw error;
    } finally {
      uiStore.setLoading(false);
    }
  }, [gameStore, uiStore, websocket]);

  // Handle FinishOption selection
  const selectFinishOption = useCallback(async () => {
    const { currentSession, currentPlayer } = gameStore;
    
    if (!currentSession || !currentPlayer) {
      uiStore.setError({ code: 'GAME_ERROR', message: 'No active session or player' });
      return;
    }

    try {
      uiStore.setLoading(true, 'Completing adventure...');
      
      await gameStore.selectFinishOption();
      
      uiStore.showToast('Adventure completed successfully!', 'success');
      
    } catch (error) {
      uiStore.setError(error as any, 'Failed to complete adventure');
      throw error;
    } finally {
      uiStore.setLoading(false);
    }
  }, [gameStore, uiStore]);


  // Join session with WebSocket integration
  const joinSession = useCallback(async (sessionId: string) => {
    try {
      uiStore.setLoading(true, 'Joining session...');
      
      // Join via REST API first
      await gameStore.joinSession(sessionId);
      
      // WebSocket will auto-connect via useWebSocket hook
      // No need to manually call websocket.joinSession here
    } catch (error) {
      uiStore.setError(error as any, 'Failed to join session');
      throw error;
    } finally {
      uiStore.setLoading(false);
    }
  }, [gameStore, uiStore]);

  // Leave session with cleanup
  const leaveSession = useCallback(() => {
    const { currentSession, currentPlayer } = gameStore;
    
    if (currentSession && currentPlayer) {
      websocket.leaveSession(currentSession.id, currentPlayer.id);
    }
    
    gameStore.leaveSession();
    websocket.disconnect();
    
    uiStore.showToast('Left session successfully', 'info');
  }, [gameStore, uiStore, websocket]);

  // Create session
  const createSession = useCallback(async (adventureId: string) => {
    try {
      uiStore.setLoading(true, 'Creating session...');
      
      const sessionId = await gameStore.createSession(adventureId);
      
      uiStore.showToast('Session created successfully!', 'success');
      return sessionId;
    } catch (error) {
      uiStore.setError(error as any, 'Failed to create session');
      throw error;
    } finally {
      uiStore.setLoading(false);
    }
  }, [gameStore, uiStore]);

  // Start session
  const startSession = useCallback(async () => {
    try {
      uiStore.setLoading(true, 'Starting session...');
      
      await gameStore.startSession();
      
      uiStore.showToast('Session started!', 'success');
    } catch (error) {
      uiStore.setError(error as any, 'Failed to start session');
      throw error;
    } finally {
      uiStore.setLoading(false);
    }
  }, [gameStore, uiStore]);

  // Load adventure
  const loadAdventure = useCallback(async (adventureId: string) => {
    try {
      uiStore.setLoading(true, 'Loading adventure...');
      
      await gameStore.loadAdventure(adventureId);
      
      uiStore.showToast('Adventure loaded successfully!', 'success');
    } catch (error) {
      uiStore.setError(error as any, 'Failed to load adventure');
      throw error;
    } finally {
      uiStore.setLoading(false);
    }
  }, [gameStore, uiStore]);

  // Update player state
  const updatePlayerState = useCallback((updates: any) => {
    gameStore.updatePlayerState(updates);
  }, [gameStore]);

  // Handle waiting conditions
  const handleWaitingCondition = useCallback((playerId: string, optionId: string) => {
    const waitingCondition = gameStore.waitingConditions.find(
      (condition: any) => condition.playerId === playerId && condition.optionId === optionId
    );
    
    if (waitingCondition) {
      uiStore.showToast(waitingCondition.message, 'warning');
    }
  }, [gameStore.waitingConditions, uiStore]);

  // Clear waiting condition
  const clearWaitingCondition = useCallback((playerId: string, optionId: string) => {
    gameStore.removeWaitingCondition(playerId, optionId);
  }, [gameStore]);

  // Check if option is available (not waiting)
  const isOptionAvailable = useCallback((optionId: string) => {
    const { currentPlayer } = gameStore;
    
    if (!currentPlayer) return false;
    
    return !gameStore.waitingConditions.some(
      (condition: any) => condition.playerId === currentPlayer.id && condition.optionId === optionId
    );
  }, [gameStore]);

  // Get player status
  const getPlayerStatus = useCallback((playerId: string) => {
    const player = gameStore.playerStates[playerId];
    const waitingCondition = gameStore.waitingConditions.find(
      (condition: any) => condition.playerId === playerId
    );
    
    return {
      player,
      isOnline: websocket.isConnected,
      isWaiting: !!waitingCondition,
      waitingMessage: waitingCondition?.message,
    };
  }, [gameStore.playerStates, gameStore.waitingConditions, websocket.isConnected]);

  // Auto-refresh game state when WebSocket connects
  useEffect(() => {
    if (websocket.isConnected && gameStore.currentSession && gameStore.currentPlayer) {
      // Refresh current scene and options
      gameStore.loadCurrentScene();
    }
  }, [websocket.isConnected, gameStore.currentSession?.id, gameStore.currentPlayer?.id]);

  return {
    // State
    currentSession: gameStore.currentSession,
    currentPlayer: gameStore.currentPlayer,
    currentAdventure: gameStore.currentAdventure,
    currentScene: gameStore.currentScene,
    availableOptions: gameStore.currentScene ? 
      gameStore.currentScene.sections.flatMap(section => 
        section.options.filter(option => gameStore.availableOptionIds.includes(option.id))
      ) : [],
    availableOptionsBySection: gameStore.currentScene ? 
      gameStore.currentScene.sections.map((section, sectionIndex) => ({
        sectionIndex,
        options: section.options.filter(option => gameStore.availableOptionIds.includes(option.id))
      })) : [],
    waitingOptions: gameStore.currentScene ? 
      gameStore.currentScene.sections.flatMap(section => 
        section.options.filter(option => gameStore.waitingOptionIds.includes(option.id))
      ) : [],
    optionsMap: gameStore.optionsMap,
    players: gameStore.players,
    playerStates: gameStore.playerStates,
    waitingConditions: gameStore.waitingConditions,
    isConnected: gameStore.isConnected,
    isLoading: gameStore.isLoading,
    error: gameStore.error,
    
    // Actions
    selectOption,
    selectFinishOption,
    joinSession,
    leaveSession,
    createSession,
    startSession,
    loadAdventure,
    loadCurrentScene: gameStore.loadCurrentScene,
    updatePlayerState,
    handleWaitingCondition,
    clearWaitingCondition,
    isOptionAvailable,
    getPlayerStatus,
    clearError: gameStore.clearError,
    clearSessionState: gameStore.clearSessionState,
    
    // WebSocket integration
    websocket,
  };
};

export default useGameState;
